import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { useThemeTokens } from '@providers/themeTokenProvider';

export type SideDrawerTokens = {
  sideDrawer: {
    drawerRootSx: SxProps<Theme>;
    headerSx: SxProps<Theme>;
    bodySx: SxProps<Theme>;
    footerSx: SxProps<Theme>;
  };
};

/**
 * Layout and surface tokens for {@link SideDrawer}. Keeps default styling out of the component
 * so consumers still override via the `sx` prop on `Drawer`.
 */
export const useSideDrawerTokens = (): SideDrawerTokens => {
  const { sys } = useThemeTokens();

  return {
    sideDrawer: {
      drawerRootSx: {
        '& .MuiDrawer-paper': {
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', sm: 'min(480px, 30vw)' },
          maxWidth: '100%',
          height: '100%',
          bgcolor: sys.color.background,
          borderLeft: `1px solid ${sys.color.outlineVariant}`,
          boxShadow: (theme: Theme) =>
            `-8px 0 24px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.35 : 0.12)}`,
        },
      },
      headerSx: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${sys.color.outlineVariant}`,
      },
      bodySx: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 2,
        py: 2,
      },
      footerSx: {
        flexShrink: 0,
        borderTop: `1px solid ${sys.color.outlineVariant}`,
        p: 1.5,
      },
    },
  };
};
